import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import CustomToast from '../../components/CustomToast';
import {
  apiErrorMessage,
  applicationPath,
  formatDate,
  formatDateTime,
  useCareersRequest,
} from './careersShared';
import {
  CareersPage, EmptyState, Loading, PageHeader, Panel, PersonAvatar, StatusBadge,
} from './careersUI';
import './careers.css';

const LIST_PATH = '/Graphe/dashboard/careers/candidates';

const CandidateDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const request = useCareersRequest();
  const toastRef = useRef(null);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoadError('');
    try {
      setData(await request(`/candidates/${id}`));
    } catch (error) {
      setLoadError(apiErrorMessage(error, 'Could not load this candidate.'));
    } finally {
      setLoading(false);
    }
  }, [id, request]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  const setActive = async (isActive) => {
    setConfirming(false);
    setBusy(true);
    try {
      const result = await request(`/candidates/${id}`, { method: 'patch', data: { is_active: isActive } });
      setData((prev) => ({ ...prev, candidate: result.candidate }));
      toastRef.current?.show('success', 'Saved', isActive ? 'Account reactivated.' : 'Account deactivated. The candidate has been signed out.');
    } catch (error) {
      toastRef.current?.show('error', 'Not saved', apiErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const back = { label: 'Candidates', onClick: () => navigate(LIST_PATH) };

  if (loading) {
    return <CareersPage><Loading /></CareersPage>;
  }
  if (loadError || !data) {
    return (
      <CareersPage>
        <PageHeader title="Candidate" back={back} />
        <EmptyState icon="pi-exclamation-circle" title={loadError || 'Candidate not found.'}>
          <Button label="Back to candidates" text onClick={() => navigate(LIST_PATH)} />
        </EmptyState>
      </CareersPage>
    );
  }

  const { candidate, applications, applicationsHidden } = data;

  return (
    <CareersPage>
      <CustomToast ref={toastRef} />
      <PageHeader title="Candidate" back={back} />

      <section className="careers-card">
        <div className="careers-profile">
          <PersonAvatar name={candidate.name} size="lg" />
          <div className="careers-profile-main">
            <div className="d-flex align-items-center flex-wrap gap-2">
              <h2 className="careers-profile-name mb-0">{candidate.name}</h2>
              {candidate.is_active
                ? <span className="careers-badge is-ok">Active</span>
                : <span className="careers-badge is-off">Deactivated</span>}
            </div>
            <div className="careers-contact">
              <a href={`mailto:${candidate.email}`}><i className="pi pi-envelope" aria-hidden="true" />{candidate.email}</a>
              {candidate.phone && <span><i className="pi pi-phone" aria-hidden="true" />{candidate.phone}</span>}
              {candidate.title && <span><i className="pi pi-briefcase" aria-hidden="true" />{candidate.title}</span>}
              {candidate.location && <span><i className="pi pi-map-marker" aria-hidden="true" />{candidate.location}</span>}
            </div>
          </div>
          {candidate.is_active ? (
            <Button label="Deactivate account" icon="pi pi-ban" severity="danger" outlined disabled={busy} onClick={() => setConfirming(true)} />
          ) : (
            <Button label="Reactivate account" icon="pi pi-check" severity="success" outlined disabled={busy} onClick={() => setActive(true)} />
          )}
        </div>
      </section>

      <Row className="g-3">
        <Col lg={4}>
          <Panel title="Account" icon="pi-id-card">
            <dl className="careers-detail-list mb-0">
              <dt>Email</dt>
              <dd>
                {candidate.email}
              </dd>
              <dt>Account Verification</dt>
              <dd><span className="careers-badge is-ok">{candidate.email_verified ? 'Verified' : 'Not verified'}</span></dd>
              <dt>Joined</dt>
              <dd>{formatDate(candidate.createdAt)}</dd>
              <dt>Last sign-in</dt>
              <dd>{formatDateTime(candidate.last_login_at)}</dd>
              {candidate.skills.length > 0 && (
                <>
                  <dt>Skills</dt>
                  <dd className="d-flex flex-wrap gap-1">{candidate.skills.map((skill) => <span key={skill} className="careers-tag">{skill}</span>)}</dd>
                </>
              )}
              {candidate.bio && (
                <>
                  <dt>About</dt>
                  <dd>{candidate.bio}</dd>
                </>
              )}
            </dl>
          </Panel>
        </Col>

        <Col lg={8}>
          <div className="careers-card is-flush">
            <div className="careers-card-head px-4 pt-3 mb-2">
              <h2 className="careers-card-title"><i className="pi pi-inbox" aria-hidden="true" />Applications ({candidate.application_count})</h2>
            </div>
            {applicationsHidden ? (
              <p className="text-muted px-4 pb-3 mb-0">You need permission to view applications to see these.</p>
            ) : applications.length === 0 ? (
              <EmptyState icon="pi-inbox" title="This candidate has not applied for any job yet." />
            ) : (
              <div className="careers-table-wrap">
                <table className="careers-table">
                  <thead>
                    <tr>
                      <th>Job</th>
                      <th>Status</th>
                      <th>Applied</th>
                    </tr>
                  </thead>
                  <tbody>
                    {applications.map((application) => (
                      <tr key={application.application_id} className="careers-clickable-row" onClick={() => navigate(applicationPath(application.application_id))}>
                        <td>
                          <span className="careers-primary-text">{application.job?.title || '—'}</span>
                          <span className="careers-meta">{application.job?.category || ''}</span>
                        </td>
                        <td><StatusBadge status={application.status} /></td>
                        <td className="is-num">{formatDate(application.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Col>
      </Row>

      <Dialog
        header={`Deactivate ${candidate.name}?`}
        visible={confirming}
        style={{ width: '28rem', maxWidth: '95vw' }}
        className="careers-dialog"
        modal
        onHide={() => setConfirming(false)}
        footer={(
          <div>
            <Button label="Cancel" text severity="secondary" onClick={() => setConfirming(false)} />
            <Button label="Deactivate" severity="danger" onClick={() => setActive(false)} autoFocus />
          </div>
        )}
      >
        <p className="mb-0">
          They will be signed out and can’t sign in or apply until you reactivate the account. Their existing applications stay as they are.
        </p>
      </Dialog>
    </CareersPage>
  );
};

export default CandidateDetail;
